import { LitElement, css, html } from 'lit'
import { property } from 'lit/decorators.js'
import '@spectrum-web-components/bundle/elements.js'
import { OpeningHours } from '../../types/opening-hours'
import { API_ENDPOINTS } from '../../../../config/api.config'
import { ApiService } from '../../../../utils/api'
import { ClosingTimeResponse } from '../../../../utils/types/api-types'

/**
 * Annahmen:
 * - Reguläre Öffnungszeiten besitzen das property "dayOfWeek" (z. B. "https://schema.org/Monday").
 * - Feiertagszeiten besitzen validFrom (und ggf. validThrough) und haben NICHT opens/closes "00:00" – sofern es
 *   nicht um einen manuellen Toggle geht.
 * - Wenn der Toggle angewandt wird, werden jeweils temporäre Stunden-Objekte erzeugt, die als
 *   "temporärer Override" gelten. Diese Objekte haben:
 *     - validFrom = heutiges Datum (im ISO-Format "YYYY-MM-DD")
 *     - opens und closes jeweils "00:00"
 *     - beim Lieferdienst zusätzlich additionalType: "delivery"
 *
 * Für die Berechnung des effektiven Zustands gilt:
 * - Zuerst wird geprüft, ob heute überhaupt ein regulärer (oder feiertagsbezogener) Öffnungszeitraum vorliegt.
 *   Ist das nicht der Fall, wird – ungeachtet von Toggle‑Objekten – als „geschlossen“ angezeigt und zusätzlich
 *   die heutigen Öffnungszeiten (z. B. "Mittwoch 12:00-18:00") eingeblendet.
 * - Ist aktuell regulär geöffnet, so liefert der Zustand:
 *     - venueOpen = true, sofern kein temporärer Restaurant‑Toggle aktiv ist.
 *     - deliveryAvailable = true, sofern kein temporärer Lieferdienst‑Toggle aktiv ist.
 * - Wird der Restaurant‑Switch betätigt, so toggeln wir beide (Restaurant und Lieferdienst);
 *   beim Lieferdienst‑Switch wird nur der zusätzliche Toggle für delivery gesetzt oder wieder entfernt.
 */

// Backend response type for checkIsOpened
interface WorkingTimeResponse {
    open: boolean
    schedule?: { openTime: string, closeTime: string }
    temporarily?: { begin: number, end: number, comment: string, deliveryOnly: boolean, showEndTime: boolean }
    holiday?: { openTime: string, closeTime: string }
}

// API response type for holiday hours
interface HolidayApiResponse {
    date: string
    opentime: string
    closetime: string
    name: string
}

// Transformed holiday entry for internal use
interface HolidayEntry {
    validFrom: string
    validThrough: string
    opens: string
    closes: string
    name: string
}

export class TempToggleOpen extends LitElement {

    @property({ type: Array })
    currentHours: OpeningHours[] = []

    @property({ type: Array })
    originalClosingTimes: any[] = []

    @property({ type: Array })
    credentials: string = ''

    @property({type: Number}) restaurantId: number | undefined

    @property({ type: Object })
    private workingStatus: WorkingTimeResponse | null = null

    @property({ type: Array })
    private holidays: HolidayEntry[] = []

    connectedCallback() {
        super.connectedCallback()
        this.fetchClosingTimes()
        this.fetchWorkingStatus()
        this.fetchHolidayHours()
    }

    updated() {
        this.shadowRoot?.querySelectorAll('sp-action-button').forEach(btn => {
            const label = btn.shadowRoot?.querySelector('#label') as HTMLElement
            if (label) {
                label.style.whiteSpace = 'normal'
                label.style.lineHeight = '1.2'
            }
        })
    }

    private async fetchWorkingStatus() {
        try {
            const numericId = this.restaurantId ? parseInt(this.restaurantId.toString(), 10) : 0
            this.workingStatus = await ApiService.get<WorkingTimeResponse>(API_ENDPOINTS.checkIsOpened(numericId))
            this.requestUpdate()
        } catch (error) {
            console.error('Error fetching working status:', error)
        }
    }

    private async fetchClosingTimes() {
        try {
            const numericId = this.restaurantId ? parseInt(this.restaurantId.toString(), 10) : 0
            const data = await ApiService.get(API_ENDPOINTS.closingTimes(numericId))
            this.mapClosingTimes(data)
        } catch (error) {
            console.error('Error fetching closing times:', error)
        }
    }

    private async fetchHolidayHours() {
        try {
            const numericId = this.restaurantId ? parseInt(this.restaurantId.toString(), 10) : 0
            const data = await ApiService.get<HolidayApiResponse[]>(API_ENDPOINTS.holidaysTimes(numericId))

            // Transform the API response into the internal format
            this.holidays = data.map((holiday: HolidayApiResponse): HolidayEntry => ({
                validFrom: holiday.date,
                validThrough: holiday.date,
                opens: holiday.opentime,
                closes: holiday.closetime,
                name: holiday.name
            }))
            this.requestUpdate()
        } catch (error) {
            console.error('Error fetching holiday hours:', error)
        }
    }

    private mapClosingTimes(data: any) {
        this.originalClosingTimes = data // Store the original data with IDs
        // Filter out old temporary closures before adding new ones
        const filteredHours = this.currentHours.filter(entry =>
            !(this.isTemporaryToggle(entry) || this.isTemporaryToggle(entry, 'delivery'))
        )
        const newHours = data.map((item: any) => ({
            validFrom: new Date(item.begin).toISOString().split('T')[0],
            validThrough: new Date(item.end).toISOString().split('T')[0],
            opens: '00:00',
            closes: '00:00',
            name: item.comment,
            additionalType: item.deliveryOnly ? 'delivery' : undefined
        }))
        this.currentHours = [...filteredHours, ...newHours]
    }

    private mapToApiFormat(entry: any): any {
        const beginTime = new Date(entry.validFrom).getTime()
        const endTime = beginTime + (365 * 24 * 60 * 60 * 1000) // Add 1 year in milliseconds
        const numericId = this.restaurantId ? parseInt(this.restaurantId.toString(), 10) : 0
        
        return {
            restaurantId: numericId,
            beginTime,
            endTime,
            showEndTime: false,
            comment: 'Vorübergehend geschlossen',
            deliveryOnly: entry.additionalType === 'delivery'
        }
    }


    private async saveClosingTime(entry: any): Promise<ClosingTimeResponse | null> {
        const apiEntry = this.mapToApiFormat(entry)
        try {
            const data = await ApiService.request<ClosingTimeResponse>(
                API_ENDPOINTS.addClosingTime(),
                {
                    method: 'POST',
                    body: apiEntry,
                    headers: { 'Authorization': this.credentials }
                }
            )
            console.log('Closing time saved successfully')
            return data
        } catch (error) {
            console.error('Error saving closing time:', error)
            return null
        }
    }

    private async removeClosingTime(id: number) {
        try {
            await ApiService.request(
                API_ENDPOINTS.removeClosingTime(),
                {
                    method: 'POST',
                    body: { id },
                    headers: { 'Authorization': this.credentials }
                }
            )
            console.log('Closing time removed successfully')
        } catch (error) {
            console.error('Error removing closing time:', error)
        }
    }

    private get todayStr(): string {
        const now = new Date()
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    }

    private isTemporaryToggle(entry: any, category?: 'delivery'): boolean {
        if (entry.validFrom !== this.todayStr) return false
        if (entry.opens !== "00:00" || entry.closes !== "00:00") return false
        return category === 'delivery' ? entry.additionalType === 'delivery' : !entry.additionalType
    }

    private get isRestaurantTemporarilyClosed(): boolean {
        const now = Date.now()
        return this.originalClosingTimes.some(entry =>
            entry.deliveryOnly === false &&
            now >= entry.begin && now <= entry.end
        )
    }

    private get isDeliveryTemporarilyUnavailable(): boolean {
        const now = Date.now()
        return this.originalClosingTimes.some(entry =>
            entry.deliveryOnly === true &&
            now >= entry.begin && now <= entry.end
        )
    }

    /**
     * Checks if today is a holiday based on fetched holidays data.
     * This is used to show "Feiertag" label even when the restaurant is closed on holidays.
     */
    private get isTodayAHoliday(): boolean {
        return this.holidays.some(holiday => this.todayStr === holiday.validFrom)
    }

    /**
     * Checks if today is a holiday where the restaurant is closed (00:00-00:00).
     */
    private get isTodayAClosedHoliday(): boolean {
        return this.holidays.some(holiday => {
            if (holiday.validFrom !== this.todayStr) return false
            return holiday.opens === "00:00" && holiday.closes === "00:00"
        })
    }

    /**
     * Gets all holiday schedules for today from fetched holidays data.
     * Returns an array of time ranges (excludes 00:00-00:00 closed entries).
     */
    private getHolidaySchedulesForToday(): Array<{ opens: string, closes: string }> {
        return this.holidays
            .filter(holiday => {
                if (holiday.validFrom !== this.todayStr) return false
                // Exclude closed entries (00:00-00:00) from time ranges
                if (holiday.opens === "00:00" && holiday.closes === "00:00") return false
                return true
            })
            .map(h => ({ opens: h.opens, closes: h.closes }))
    }

    private getScheduleForToday(): Array<{ opens: string, closes: string }> {
        const holidaySchedules = this.currentHours.filter(entry => {
            if (!entry.validFrom) return false
            if (entry.opens === "00:00" && entry.closes === "00:00") return false
            if (entry.validThrough) {
                return this.todayStr >= entry.validFrom && this.todayStr <= entry.validThrough
            } else {
                return this.todayStr === entry.validFrom
            }
        })

        if (holidaySchedules.length > 0) {
            return holidaySchedules.map(entry => ({ opens: entry.opens, closes: entry.closes }))
        }

        const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' })
        const regularSchedules = this.currentHours.filter(entry => {
            return entry.dayOfWeek && entry.dayOfWeek.endsWith(todayName)
        })
        return regularSchedules.map(entry => ({ opens: entry.opens, closes: entry.closes }))
    }

    get scheduledOpen(): boolean {
        const schedule = this.getScheduleForToday()
        if (schedule.length === 0) return false
        if (schedule.length === 1 && (schedule[0].opens === '00:00' && schedule[0].closes === '00:00')) return false
        
        return schedule.some(interval => this.isNowWithinInterval(interval.opens, interval.closes))
    }

    private isNowWithinInterval(opens: string, closes: string): boolean {
        const now = new Date()
        const [openHour, openMinute] = opens.split(':').map(Number)
        const [closeHour, closeMinute] = closes.split(':').map(Number)
        let openDate = new Date(now)
        openDate.setHours(openHour, openMinute, 0, 0)
        let closeDate = new Date(now)
        closeDate.setHours(closeHour, closeMinute, 0, 0)
        if (closeDate <= openDate) {
            closeDate.setDate(closeDate.getDate() + 1)
        }
        return now >= openDate && now <= closeDate
    }

    private get currentStatus(): 'closed' | 'no-delivery' | 'open' {
        if (this.isRestaurantTemporarilyClosed) return 'closed'
        if (this.isDeliveryTemporarilyUnavailable) return 'no-delivery'
        return 'open'
    }

    private async setStatus(newStatus: 'closed' | 'no-delivery' | 'open') {
        console.log('setStatus called with:', newStatus, 'current:', this.currentStatus, 'scheduledOpen:', this.scheduledOpen)

        if (newStatus === this.currentStatus) {
            console.log('Blocked: same status')
            return
        }

        // Remove all existing closures
        console.log('Removing closures:', this.originalClosingTimes)
        for (const entry of this.originalClosingTimes) {
            await this.removeClosingTime(entry.id)
        }
        this.originalClosingTimes = []
        let newHours = this.currentHours.filter(entry =>
            !(this.isTemporaryToggle(entry) || this.isTemporaryToggle(entry, 'delivery'))
        )

        // Create new closure if needed
        if (newStatus === 'closed' || newStatus === 'no-delivery') {
            const closure = {
                validFrom: this.todayStr,
                opens: '00:00',
                closes: '00:00',
                name: 'temporaryClosure',
                additionalType: newStatus === 'no-delivery' ? 'delivery' : undefined
            }
            console.log('Saving closure:', closure)
            await this.saveClosingTime(closure)
        }

        // Refetch to get correct state from server
        await this.fetchClosingTimes()
        await this.fetchWorkingStatus()
        this.requestUpdate()
        this.dispatchEvent(new CustomEvent('temporaryhourschange', { detail: newHours }))
    }

    private sortByOpeningTime(schedules: Array<{ opens: string, closes: string }>): Array<{ opens: string, closes: string }> {
        return [...schedules].sort((a, b) => a.opens.localeCompare(b.opens))
    }

    get todaysHours(): string {
        // Check if today is a closed holiday (00:00-00:00) - show "geschlossen"
        if (this.isTodayAClosedHoliday) {
            return 'geschlossen'
        }

        // Get all holiday schedules for today (supports multiple time ranges)
        const holidaySchedules = this.getHolidaySchedulesForToday()
        if (holidaySchedules.length > 0) {
            const sorted = this.sortByOpeningTime(holidaySchedules)
            const intervals = sorted.map(s => `${s.opens}-${s.closes}`)
            return `geöffnet von ${intervals.join(' und ')}`
        }

        // Use local schedule calculation (supports multiple time ranges)
        const schedule = this.getScheduleForToday()
        if (schedule.length === 0) {
            return this.workingStatus ? 'geschlossen' : ''
        }
        const sorted = this.sortByOpeningTime(schedule)
        const intervals = sorted.map(interval => `${interval.opens}-${interval.closes}`)
        return intervals[0] === '00:00-00:00' ? 'geschlossen' : `geöffnet von ${intervals.join(' und ')}`
    }

    get todaysHoursText(): string {
        // Check if today is a holiday (backend OR local data)
        if (this.workingStatus?.holiday || this.isTodayAHoliday) {
            return `heute (Feiertag) `
        }
        // Regular day
        const schedule = this.getScheduleForToday()
        if (schedule.length === 0) return ''
        return `heute regulär `
    }

    get isActuallyOpen(): boolean {
        // Check local temporary closure first (same order as currentStatusText)
        if (this.isRestaurantTemporarilyClosed) return false

        // Use backend status for schedule/holiday determination
        if (this.workingStatus) {
            return this.workingStatus.open
        }
        // Fallback to local calculation
        return this.scheduledOpen
    }

    get currentStatusText(): string {
        if (this.isRestaurantTemporarilyClosed) {
            return 'geschlossen'
        }
        if (this.isDeliveryTemporarilyUnavailable) {
            return 'geöffnet (kein Lieferdienst)'
        }
        // Use isActuallyOpen for consistency
        return this.isActuallyOpen ? 'geöffnet' : 'geschlossen'
    }

    get statusColorClass(): string {
        if (this.isRestaurantTemporarilyClosed) {
            return 'status-closed'
        }
        if (this.isDeliveryTemporarilyUnavailable) {
            return 'status-no-delivery'
        }
        return this.isActuallyOpen ? 'status-open' : 'status-closed'
    }

    render() {
        return html`
            <sp-action-group
                compact
                selects="single"
                .selected=${[this.currentStatus]}
                @change=${(e: Event) => {
                    const group = e.target as any
                    const selected = Array.isArray(group.selected) ? group.selected[0] : group.selected
                    console.log('Change event, selected:', group.selected, '→', selected)
                    if (selected) this.setStatus(selected)
                }}>
                <sp-action-button value="closed">Geschlossen</sp-action-button>
                <sp-action-button value="no-delivery">Kein Lieferdienst</sp-action-button>
                <sp-action-button value="open">Geöffnet (reguläre Öffnungszeiten)</sp-action-button>
            </sp-action-group>
            <!-- <p class="${this.statusColorClass}">
                Aktuell: ${this.currentStatusText}
            </p> -->
            <p>${this.todaysHoursText}${this.todaysHours}</p>
        `
    }

    static styles = css`
    :host {
      display: block;
      text-align: center;
      width: 100%;
    }
    sp-icon-info {
      color: #5483d0;
    }
    sp-action-group {
      font-family: 'Asap Condensed';
      display: inline-flex;
      flex-wrap: wrap;
      gap: 0;
      justify-content: center;
      max-width: 100%;
    }
    sp-action-button {
      font-family: 'Asap Condensed';
      --mod-actionbutton-font-size: 15px;
      flex-shrink: 1;
      --mod-actionbutton-min-width: 0;
      min-width: 0;
    }
    @media (max-width: 400px) {
      sp-action-button {
        --mod-actionbutton-font-size: 13px;
      }
    }
    sp-action-button[selected] {
      color: white;
      --spectrum-actionbutton-content-color-default: white;
    }
    .hint {
      color: gray;
      justify-content: center;
    }
    h1,h2,h3,h4,h5 {
      margin-top: 0;
      font-family: 'Asap Condensed';
    }
    p {
      font-family: 'Asap Condensed';
    }
    .status-open {
      color: #28a745;
      font-weight: bold;
    }
    .status-closed {
      color: #dc3545;
      font-weight: bold;
    }
    .status-no-delivery {
      color: #ffc107;
      font-weight: bold;
    }
    /* Toggle button colors based on selection */
    sp-action-button[value="open"][selected] {
      background-color: #28a745 !important;
      --spectrum-actionbutton-background-color-default: #28a745;
    }
    sp-action-button[value="closed"][selected] {
      background-color: #dc3545 !important;
      --spectrum-actionbutton-background-color-default: #dc3545;
    }
    sp-action-button[value="no-delivery"][selected] {
      background-color: #ffc107 !important;
      --spectrum-actionbutton-background-color-default: #ffc107;
      color: #000 !important;
      --spectrum-actionbutton-content-color-default: #000;
    }
  `
}

if (!customElements.get('temp-toggle-open')) {
    customElements.define('temp-toggle-open', TempToggleOpen)
}

declare global {
    interface HTMLElementTagNameMap {
        'temp-toggle-open': TempToggleOpen
    }
}
